import { basicSorter } from '@avantia/client-and-server-utilities';
import { lodash } from '@avantia/lodash';
import { HpControlActionTypes } from '@avantia/questionset-model';
import { BaseSyntheticEvent, Component, FocusEvent, FormEvent, MouseEvent } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { HpDynamicButtonClickPayload } from '../../actions/dynamicButtonClickActionTypes';
import { addToActionHistory } from '../../addToActionHistoryRegistry';
import { HpClientDebugFlags } from '../../clientDebugFlags';
import { logInfo } from '../../clientLogging';
import { HpMetaData, getMetaDataOrUndefinedFromAnswers } from '../../tools/clientAndServerUtilities';
import { forEachSectionInOrder } from '../../tools/questionDisplayStateTools';
import {
  HpClientQuestionData,
  HpClientQuestionLookupItemMap,
  HpClientQuestionMap,
  HpClientSection,
  HpUserInterfaceState
} from '../../types';
import { HpEventDetail } from '../../types/inputControlTypes';
import { HpQuestionSetPageProps } from '../../types/questionSetPagePropTypes';
import { BasicDropDownListControl } from '../formControls/common/basicDropDownListControl';
import { getNavigationTabControl } from '../formControls/common/getNavigationTabControl';
import {
  HpGetNavigationTabControlFunction,
  HpNavigationTabProps
} from '../formControls/common/navigationTabControlTypes';
import { createFormEvent } from '../formControls/inputControl';
import { getHeadingControl } from '../headings/getHeadingControl';
import { HpHeadingControlProps } from '../headings/headingTypes';
import { addDisplaySizeClassToBodyElement, maybeShowErrorPageOrLoadData } from '../pageComponentUtilities';
import { ModalWidget } from '../widgets';
import { InvokeScriptActionsWidget } from '../widgets/invokeScriptActionsWidget';
import { ValidationSummaryWidget } from '../widgets/validationSummaryWidget';
import { EventProcessor } from './eventProcessor';
import { FluentFieldProcessor } from './fluentFieldProcessor';
import {
  applyStylesheetsToPage,
  createNonQuestion,
  createQuestionControls,
  getLoadingPageElement,
  mapDispatchToPropsDefault,
  mapStateToPropsDefault
} from './questionSetPageFunctions';
import {
  HpComponentExtraActionFunction,
  HpComponentExtraActionProps,
  HpCreateQuestionSetPageProps,
  HpPageEvents,
  HpRenderSideBarProps
} from './questionSetPageTypes';
import { StandardFieldProcessor } from './standardFieldProcessor';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function createQuestionSetPage({
  pageType: pageTypeMaybe,
  createHiddenFormFields,
  createSideBar,
  createQuestionPanel,
  mapDispatchToProps,
  mapStateToProps,
  LoadingWidget,
  componentDidMountExtra,
  componentDidUpdateExtra
}: HpCreateQuestionSetPageProps) {
  const formName = 'QuestionSet';
  const debugMode = true;
  const pageType = pageTypeMaybe || 'questions';

  class QuestionSetPage<PagePropsT extends HpQuestionSetPageProps = HpQuestionSetPageProps>
    extends Component<PagePropsT>
    implements HpPageEvents
  {
    public readonly buttonClicked: (
      event: React.MouseEvent<HTMLElement, globalThis.MouseEvent>,
      detail: HpEventDetail
    ) => void;

    public readonly fieldChanged: (
      event: React.FormEvent<HTMLElement> | React.FocusEvent<HTMLElement, Element>,
      detail: HpEventDetail
    ) => void;

    public readonly fieldClicked: (
      event: React.MouseEvent<HTMLElement, globalThis.MouseEvent>,
      detail: HpEventDetail
    ) => void;

    constructor(props: PagePropsT) {
      super(props);
      const eventProcessor = new EventProcessor<PagePropsT>(formName, debugMode, [
        {
          name: 'StandardFieldProcessor',
          processor: new StandardFieldProcessor()
        },
        {
          name: 'FluentFieldProcessor',
          processor: new FluentFieldProcessor()
        }
      ]);
      const { buttonClicked, componentDidMount, componentDidUpdate, fieldChanged, fieldClicked } =
        createPageEventFunctions(this, pageType, eventProcessor, componentDidMountExtra, componentDidUpdateExtra);
      this.buttonClicked = buttonClicked;
      this.fieldChanged = fieldChanged;
      this.fieldClicked = fieldClicked;
      this.componentDidMount = componentDidMount;
      this.componentDidUpdate = componentDidUpdate;
    }

    public render(): JSX.Element {
      const { questions, answers, productId, displayState } = this.props;
      const { questionsUI } = displayState;
      const {
        activeNavigationKey,
        availableQuestionSets,
        headerProps,
        highestTouchedNavigationKey,
        isDesktop,
        isJourneyComplete,
        loadingPageWidget,
        metaData,
        navigationSections,
        qsData,
        question,
        questionControls,
        questionControlMap,
        questionSetCount,
        showValidationSummary,
        sideBarProps,
        validationResult,
        viewport,
        NavigationTabControl
      } = createRenderData(this, pageType);

      const sideBar = createSideBar(sideBarProps);

      const ui = questionsUI[question.questionId];

      return (
        <>
          {createHeadingComponent(headerProps)}
          <Row>
            <Col xs={12}>
              <form>
                <Container className="rounded">
                  {questionSetCount > 1 && (
                    <Row>
                      <Col>
                        <BasicDropDownListControl
                          answers={answers}
                          eventDetail={{ type: 'standard', question }}
                          id="questionSetId"
                          lookup={qsData.lookup}
                          name=""
                          productId={productId}
                          sortItemsBy={undefined}
                          value={availableQuestionSets.selected || ''}
                          onClick={createFormEvent(this.fieldChanged, {
                            type: 'questionset',
                            question
                          })}
                          ui={ui || {}}
                        />
                      </Col>
                    </Row>
                  )}
                  <Row>
                    <Col>
                      <NavigationTabControl
                        activeKey={activeNavigationKey}
                        eventDetail={{ type: 'nav-bar', question }}
                        hasError={validationResult.count > 0}
                        highestTouchedKey={highestTouchedNavigationKey}
                        id="mainNavBar"
                        isJourneyComplete={isJourneyComplete}
                        items={navigationSections}
                        onClick={this.buttonClicked}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={isDesktop && sideBar ? 8 : 12}>
                      <div className="av-card" id="hp-questions-container" tabIndex={-1}>
                        {createInnerHeadingComponent(headerProps)}
                        {showValidationSummary && validationResult.count > 0 && (
                          <ValidationSummaryWidget validationResult={validationResult} location={location} />
                        )}
                        {createQuestionPanel
                          ? createQuestionPanel({
                              questionMap: questionControlMap,
                              questions,
                              answers,
                              productId,
                              pageProps: this.props,
                              pageType,
                              pageEvents: {
                                buttonClicked: this.buttonClicked,
                                fieldChanged: this.fieldChanged,
                                fieldClicked: this.fieldClicked,
                                props: this.props
                              },
                              viewport
                            })
                          : questionControls}
                      </div>
                    </Col>
                    {isDesktop && sideBar}
                  </Row>
                  {!isDesktop && !!sideBar && (
                    <Row>
                      <Col>{sideBar}</Col>
                    </Row>
                  )}
                </Container>
                {createHiddenFormFields && createHiddenFormFields({ metaData })}
              </form>
            </Col>
          </Row>

          <InvokeScriptActionsWidget scriptActions={this.props.displayState.scriptActions} />
          <ModalWidget questionSet={this.props} pageProps={this.props} />
          {loadingPageWidget}
        </>
      );
    }
  }

  interface CreateHeadingControlProps extends HpHeadingControlProps {
    headingControlType: string | undefined;
  }

  function createHeadingComponent(props: CreateHeadingControlProps): JSX.Element | null {
    return createHeadingControlType(props, '');
  }

  function createInnerHeadingComponent(props: CreateHeadingControlProps): JSX.Element | null {
    return createHeadingControlType(props, 'Inner');
  }

  function createHeadingControlType(props: CreateHeadingControlProps, type: 'Inner' | ''): JSX.Element | null {
    const { headingControlType = 'Default' } = props;
    const HeadingControl = getHeadingControl(headingControlType + type);
    return HeadingControl ? <HeadingControl {...props} /> : null;
  }

  function createCompleteJourney<PagePropsT extends HpQuestionSetPageProps>(
    questions: HpClientQuestionMap,
    pageProps: PagePropsT
  ): React.MouseEventHandler<HTMLElement> {
    const completeJourney: React.MouseEventHandler<HTMLElement> = (event) => {
      const { dynamicButtonClick, redirectToErrorPage } = pageProps;
      const { location } = pageProps as unknown as RouteComponentProps;
      const questionIds = lodash.keys(questions);
      const requiredAction: HpControlActionTypes = 'submit-form';
      for (const questionId of questionIds) {
        const question = questions[questionId];
        const { dependentActions } = question.data || {};
        if (dependentActions && dependentActions.filter((action) => action.action === requiredAction).length > 0) {
          const payload: HpDynamicButtonClickPayload = {
            getEvent: () => event,
            formName: 'QuestionSet',
            question,
            questionId,
            pageProps,
            groupNumber: undefined,
            indexes: undefined,
            key: undefined,
            isNavigation: true
          };
          return dynamicButtonClick(payload);
        }
      }

      redirectToErrorPage({
        location,
        message: `No ${requiredAction} action could be found in the question set. Journey cannot be completed.`
      });
    };

    return completeJourney;
  }

  function createPageEventFunctions<PagePropsT extends HpQuestionSetPageProps = HpQuestionSetPageProps>(
    self: { props: PagePropsT },
    pageType: string,
    eventProcessor: EventProcessor<PagePropsT>,
    componentDidMountExtra: HpComponentExtraActionFunction | undefined,
    componentDidUpdateExtra: HpComponentExtraActionFunction | undefined
  ) {
    const componentDidMount = (): void => {
      runComponentExtraAndPageLoadData({
        pageType,
        eventType: 'componentDidMount',
        actionProps: { currProps: self.props, prevProps: null, self },
        extraAction: componentDidMountExtra
      });
    };

    const componentDidUpdate = (prevProps: PagePropsT): void => {
      if (prevProps === self.props) {
        return;
      }

      runComponentExtraAndPageLoadData({
        pageType,
        eventType: 'componentDidUpdate',
        actionProps: { currProps: self.props, prevProps, self },
        extraAction: componentDidUpdateExtra
      });
    };

    const buttonClicked = (event: MouseEvent<HTMLElement>, detail: HpEventDetail): void => {
      eventProcessor.processFormEvent(preventDefault(event), detail || { type: undefined }, self.props);
    };

    const fieldChanged = (event: FormEvent<HTMLElement> | FocusEvent<HTMLElement>, detail: HpEventDetail): void => {
      eventProcessor.processFormEvent(preventDefault(event), detail || { type: undefined }, self.props);
    };

    const fieldClicked = (event: MouseEvent<HTMLElement>, detail: HpEventDetail): void => {
      eventProcessor.processMouseEvent(preventDefault(event), detail || { type: undefined }, self.props);
    };

    const map = {
      componentDidMount,
      componentDidUpdate,
      buttonClicked,
      fieldChanged,
      fieldClicked
    };
    lodash.keys(map).forEach((key) => {
      map[key] = map[key].bind(self);
    });
    return map;
  }

  function createRenderData<PagePropsT extends HpQuestionSetPageProps = HpQuestionSetPageProps>(
    self: HpPageEvents & {
      props: PagePropsT;
    },
    pageType: string
  ) {
    const {
      answers,
      displayState,
      friendlyName,
      headingControlType,
      productId,
      questionFormats,
      questions,
      userInterface,
      validationResult
    } = self.props;

    const { visibleQuestionIds, availableQuestionSets, showValidationSummary, highestSectionStartedPosition } =
      displayState;

    const { ajaxCallsInProgress, ajaxCallsMap, viewport } = userInterface;
    const loadingPageWidget = getLoadingPageElement(ajaxCallsInProgress, ajaxCallsMap, viewport, LoadingWidget);
    const { elements: questionControls, elementMap: questionControlMap } = createQuestionControls({
      allAnswers: answers,
      allQuestions: questions,
      displayState,
      level: 0,
      page: self,
      pageType,
      productId,
      questionFormats,
      questionIdNodes: visibleQuestionIds,
      validationResult,
      viewport
    });

    let questionSetCount = 0;
    const availableQuestionSetOptions: HpClientQuestionLookupItemMap = {};
    availableQuestionSets.items
      .sort(({ productDescription: descA, productId: idA }, { productDescription: descB, productId: idB }) => {
        let res = basicSorter(descA, descB);
        if (res === 0) {
          res = basicSorter(idA, idB);
        }

        return res;
      })
      .forEach(({ productId, friendlyName, productDescription }) => {
        questionSetCount++;
        availableQuestionSetOptions[productId] = {
          text: productDescription
            ? `${productDescription}${friendlyName && friendlyName !== productDescription ? ` - ${friendlyName}` : ''}`
            : productId
        };
      });

    const qsData: HpClientQuestionData = {
      lookup: availableQuestionSetOptions,
      validation: [],
      textAndStyle: {
        standard: {
          questionText: 'Question set:',
          controlType: 'DropDownList'
        }
      }
    };

    let activeNavigationKey = '';
    let highestTouchedNavigationKey = '';
    const navigationSections: HpNavigationTabProps[] = [];

    forEachSectionInOrder({
      displayState,
      options: { iterate: 'sections-only' },
      worker: ({ section, sectionUI, sectionId, isActive }) => {
        if (sectionUI.isVisible) {
          navigationSections.push({ key: sectionId, name: section.title });
          if (isActive) {
            activeNavigationKey = sectionId;
          }

          if (highestSectionStartedPosition === section.position) {
            highestTouchedNavigationKey = sectionId;
          }
        }
      }
    });

    const question = createNonQuestion();
    const { activeSectionId, sections } = displayState;
    const activeSection: HpClientSection = sections[activeSectionId];
    const { isDesktop } = viewport;
    const metaData = getMetaDataOrUndefinedFromAnswers(productId, answers);
    const sideBarProps: HpRenderSideBarProps = { isDesktop, metaData, answers, activeSectionId };
    const isJourneyComplete: boolean = !!metaData && metaData.isJourneyComplete === true;
    const getNavControl: HpGetNavigationTabControlFunction<HpMetaData> = getNavigationTabControl(headingControlType);
    const NavigationTabControl = getNavControl({
      metaData,
      viewport,
      answers,
      completeJourney: createCompleteJourney(questions, self.props)
    });

    let sectionSubheading: string | null = null;
    let sectionHeading: string | null = null;
    if (activeSection) {
      sectionSubheading = activeSection.subheading || null;
      sectionHeading = activeSection.heading || null;
    }

    const headerProps: CreateHeadingControlProps = {
      friendlyName,
      headingControlType,
      sectionHeading,
      sectionSubheading,
      sectionTitle: activeSection ? activeSection.title : '',
      answers,
      isLoading: visibleQuestionIds.length <= 0
    };

    return {
      activeNavigationKey,
      availableQuestionSets,
      headerProps,
      highestTouchedNavigationKey,
      isDesktop,
      isJourneyComplete,
      loadingPageWidget,
      metaData,
      navigationSections,
      qsData,
      question,
      questionControls,
      questionControlMap,
      questionSetCount,
      showValidationSummary,
      sideBarProps,
      validationResult,
      viewport,
      NavigationTabControl
    };
  }

  function preventDefault<EventT extends BaseSyntheticEvent>(event: EventT): EventT {
    // The preventDefault is undefined in blur event for AutoCompleteListControl.
    if (event.preventDefault) {
      event.preventDefault();
    }

    return event;
  }

  const connected = connect(mapStateToProps || mapStateToPropsDefault, mapDispatchToProps || mapDispatchToPropsDefault);
  const ref = connected(QuestionSetPage<HpQuestionSetPageProps & RouteComponentProps>);
  return withRouter(ref);
}

export interface HpRunComponentExtraAndPageLoadDataProps<PagePropsT = HpQuestionSetPageProps> {
  pageType: string;
  eventType: 'componentDidMount' | 'componentDidUpdate';
  actionProps: HpComponentExtraActionProps<PagePropsT>;
  extraAction?: HpComponentExtraActionFunction<PagePropsT> | undefined;
  loadData?: false;
}

export function runComponentExtraAndPageLoadData<
  PagePropsT extends { userInterface: HpUserInterfaceState } = HpQuestionSetPageProps
>({
  pageType,
  eventType,
  actionProps,
  extraAction,
  loadData: maybeLoadData
}: HpRunComponentExtraAndPageLoadDataProps<PagePropsT>): void {
  const eventName = `${pageType}:${eventType}`;

  logInfo(eventName, HpClientDebugFlags.PageLoadDataHandler);
  addToActionHistory(eventType, { pageType });

  const { prevProps, currProps } = actionProps;
  const loadData = maybeLoadData !== false;

  if (extraAction) {
    if (!extraAction(actionProps)) {
      if (loadData) {
        maybeShowErrorPageOrLoadData<PagePropsT>(currProps);
      }
    } else {
      logInfo(
        `The maybeShowErrorPageOrLoadData() will not be run because ${eventType}Extra() returned true`,
        HpClientDebugFlags.PageLoadDataHandler
      );
    }
  } else if (loadData) {
    maybeShowErrorPageOrLoadData<PagePropsT>(currProps);
  }

  addDisplaySizeClassToBodyElement({ userInterface: currProps.userInterface, pageType });

  if (prevProps && currProps && getStylesheets(prevProps) !== getStylesheets(currProps)) {
    applyStylesheetsToPage(getStylesheets(currProps) || []);
  }
}

export function getStylesheets(props: any): string[] | undefined {
  const { stylesheets } = props as {
    stylesheets?: string[];
  };
  return stylesheets;
}
